import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye, Users, Globe, Activity, Clock, ExternalLink, Shield, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import api from '../utils/api';

const FLAG = (code) => {
  if (!code || code === '??' || code === 'LO') return '';
  return String.fromCodePoint(...[...code.toUpperCase()].map(c => 0x1F1E6 + c.charCodeAt(0) - 65));
};

const AdminPage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get('/admin/stats')
      .then(res => setStats(res.data))
      .catch(err => setError(err.response?.status === 403 ? 'Admin access required' : 'Failed to load stats'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 text-red-400/50 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-foreground mb-2">{error}</h2>
          <Button variant="outline" onClick={() => navigate('/dashboard')} className="mt-4 border-input text-foreground/80">
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  const ChartTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-muted border border-input rounded-lg px-3 py-2 shadow-xl">
        <p className="text-white text-xs font-medium">{label}</p>
        {payload.map((p, i) => (
          <p key={i} className="text-xs" style={{ color: p.color }}>{p.name}: {p.value}</p>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      <div className="bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-2 text-foreground/80 hover:text-foreground hover:bg-accent" data-testid="admin-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>
          <div className="flex items-center gap-3">
            <Shield className="w-7 h-7 text-red-400" />
            <div>
              <h1 className="text-3xl font-bold text-foreground">Admin Dashboard</h1>
              <p className="text-muted-foreground mt-0.5">Page visits, geography, and platform analytics</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        {/* Stat Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Total Visits', value: stats.totalVisits, icon: Eye, color: 'text-blue-400', bg: 'bg-blue-500/10 border-blue-500/20' },
            { label: 'Unique Visitors', value: stats.uniqueVisitors, icon: Globe, color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20' },
            { label: 'Today Visits', value: stats.todayVisits, icon: Activity, color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/20' },
            { label: 'Today Unique', value: stats.todayUnique, icon: Users, color: 'text-purple-400', bg: 'bg-purple-500/10 border-purple-500/20' },
          ].map((stat) => (
            <Card key={stat.label} className={`${stat.bg} border`} data-testid={`admin-stat-${stat.label.toLowerCase().replace(/\s/g, '-')}`}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <stat.icon className={`w-8 h-8 ${stat.color}`} />
                  <span className={`text-3xl font-bold ${stat.color}`}>{stat.value.toLocaleString()}</span>
                </div>
                <p className="text-muted-foreground text-sm mt-2">{stat.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Platform overview */}
        <div className="grid grid-cols-2 gap-4">
          <Card className="bg-card border-border">
            <CardContent className="p-5 flex items-center gap-4">
              <Users className="w-10 h-10 text-cyan-400" />
              <div>
                <div className="text-2xl font-bold text-foreground">{stats.totalUsers}</div>
                <div className="text-muted-foreground text-sm">Registered Users</div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-5 flex items-center gap-4">
              <Activity className="w-10 h-10 text-pink-400" />
              <div>
                <div className="text-2xl font-bold text-foreground">{stats.totalProjects}</div>
                <div className="text-muted-foreground text-sm">Total Projects</div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Daily Chart */}
        <Card className="bg-card border-border">
          <CardHeader><CardTitle className="text-white text-lg">Daily Visits (Last 14 Days)</CardTitle></CardHeader>
          <CardContent>
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.dailyVisits}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#222222" />
                  <XAxis dataKey="date" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                  <YAxis tick={{ fill: '#94a3b8', fontSize: 12 }} allowDecimals={false} />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar dataKey="visits" name="Total" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="unique" name="Unique" fill="#10b981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Geography */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Visitors by Country */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <Globe className="w-5 h-5 text-blue-400" /> Visitors by Country
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-[350px] overflow-y-auto">
                {(!stats.visitorsByCountry || stats.visitorsByCountry.length === 0) ? (
                  <p className="text-muted-foreground text-sm text-center py-8">No geographic data yet. Visits will show here as traffic comes in.</p>
                ) : (
                  stats.visitorsByCountry.map((c, i) => {
                    const maxCount = stats.visitorsByCountry[0]?.count || 1;
                    return (
                      <div key={i} className="relative" data-testid={`country-${i}`}>
                        <div className="absolute inset-0 bg-blue-500/10 rounded-lg" style={{ width: `${(c.count / maxCount) * 100}%` }} />
                        <div className="relative flex items-center justify-between px-3 py-2.5">
                          <div className="flex items-center gap-2">
                            <span className="text-lg">{FLAG(c.code)}</span>
                            <span className="text-sm text-foreground font-medium">{c.country}</span>
                          </div>
                          <span className="text-sm font-bold text-blue-400">{c.count}</span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </CardContent>
          </Card>

          {/* Visitors by City */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <MapPin className="w-5 h-5 text-pink-400" /> Top Cities
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-[350px] overflow-y-auto">
                {(!stats.visitorsByCity || stats.visitorsByCity.length === 0) ? (
                  <p className="text-muted-foreground text-sm text-center py-8">No city data yet</p>
                ) : (
                  stats.visitorsByCity.map((c, i) => {
                    const maxCount = stats.visitorsByCity[0]?.count || 1;
                    return (
                      <div key={i} className="relative" data-testid={`city-${i}`}>
                        <div className="absolute inset-0 bg-pink-500/10 rounded-lg" style={{ width: `${(c.count / maxCount) * 100}%` }} />
                        <div className="relative flex items-center justify-between px-3 py-2.5">
                          <div className="flex items-center gap-2">
                            <MapPin className="w-3.5 h-3.5 text-pink-400" />
                            <span className="text-sm text-foreground">{c.city}</span>
                            <span className="text-xs text-muted-foreground">{c.country}</span>
                          </div>
                          <span className="text-sm font-bold text-pink-400">{c.count}</span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Visits + Top Referrers */}
        <div className="grid lg:grid-cols-2 gap-6">
          <Card className="bg-card border-border">
            <CardHeader><CardTitle className="text-white text-lg">Recent Visits</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {stats.recentVisits.length === 0 ? (
                  <p className="text-muted-foreground text-sm text-center py-8">No visits recorded yet</p>
                ) : (
                  stats.recentVisits.map((v, i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-2 bg-muted rounded-lg border border-border" data-testid={`recent-visit-${i}`}>
                      <div className="flex items-center gap-3">
                        <span className="text-base">{FLAG(v.country_code)}</span>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-white text-sm font-medium">{v.page}</span>
                            {v.city && v.city !== 'Unknown' && (
                              <span className="text-xs text-muted-foreground">{v.city}, {v.country}</span>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground mt-0.5">{v.ip}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Clock className="w-3 h-3 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">
                          {v.timestamp ? new Date(v.timestamp).toLocaleString() : '—'}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardHeader><CardTitle className="text-white text-lg">Top Referrers</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2">
                {stats.topReferrers.length === 0 ? (
                  <p className="text-muted-foreground text-sm text-center py-8">No referrer data yet</p>
                ) : (
                  stats.topReferrers.map((ref, i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-3 bg-muted rounded-lg border border-border">
                      <div className="flex items-center gap-2 min-w-0">
                        <ExternalLink className="w-4 h-4 text-blue-400 flex-shrink-0" />
                        <span className="text-sm text-foreground/80 truncate">{ref.referrer}</span>
                      </div>
                      <span className="text-sm font-bold text-white ml-3">{ref.count}</span>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
